import { useCallback, useEffect, useState } from 'react';
import type { ApiClient } from '../api';
import { IconWarnung } from '../Icons';
import {
  ContentSection, DataTable, EmptyState, ErrorState, LoadingState,
  PropertyGrid, StatusBadge, type Spalte,
} from '../muster';
import {
  artname, bauartname, herkunftname, paarungname, raumangabe,
  schottherkunftname, zulassungsstand, zulassungsstandZustand,
} from '../aussparungKlartext';
import { AnforderungslisteKnopf } from './aussparungen/AnforderungslisteKnopf';
import { AussparungsplanKnopf } from './aussparungen/AussparungsplanKnopf';

/**
 * Die Arbeitsansicht der Aussparungen.
 *
 * --- Warum nach Geschoss UND Planungsbereich -------------------------------
 *
 * Die Planung läuft abschnittsweise. Wer Abschnitt 3 im zweiten Obergeschoss
 * bearbeitet, braucht genau diese Aussparungen — nicht die des Geschosses
 * und nicht die des Abschnitts über alle Etagen.
 *
 * --- Warum die Detailansicht diese Reihenfolge hat -------------------------
 *
 * Von oben nach unten, wie man eine Aussparung prüft:
 *
 *   1. WAS ist es           Kennung, Maße, Form
 *   2. WO liegt es          Geschoss, Bereich, von Raum nach Raum
 *   3. WAS geht durch       die Medien mit Nennweite und Werkstoff
 *   4. WAS wird durchbrochen  die Wand mit Bauart und Dicke
 *   5. WIE steht es         der Freigabestand mit dem Schriftwechsel
 *   6. WAS sagt die Architektur  die Verknüpfung zum ARDU-Modell
 *   7. WOMIT wird geschottet  Systeme und Zulassungen
 *
 * Die Zulassung steht unten und nicht oben: Sie ist das Ergebnis, und wer
 * sie prüft, hat die sechs Zeilen darüber schon gelesen.
 */

interface Bereich {
  planungsbereich: string | null;
  quadrant: string | null;
  stueck: number;
  ohneAnforderung: number;
}

// Die Namen stehen in ../aussparungKlartext — ein Wörterbuch für alle
// vier Aussparungsseiten, das PDF und die LV-Ausgabe.

// Die Namen stehen in ../aussparungKlartext — ein Wörterbuch für alle
// vier Aussparungsseiten, das PDF und die LV-Ausgabe.

/**
 * Die Marke „TT-Platte" hinter der Nummer (Fachplaner, 23.09.2026): Wer den
 * Arbeitsvorrat durchgeht, soll ohne Klick sehen, welche Aussparung in einer
 * TT-Platte liegt — dort gelten andere Schottregeln und andere Bohrgrenzen.
 * Die Bauart kommt vom durchbrochenen Bauteil (`tt_platte`), nicht aus dem
 * Namen; ein zweiter Host in Stahlbeton hebt die Marke nicht auf.
 */
export function TtPlatteMarke() {
  return (
    <span
      title="Die Aussparung liegt in einer TT-Platte (Bauart des durchbrochenen Bauteils)"
      style={{
        marginLeft: 6, padding: '0 5px', borderRadius: 3, fontSize: 10,
        fontWeight: 600, letterSpacing: 0.3, verticalAlign: 'middle',
        background: 'rgba(124, 58, 237, 0.14)', color: '#7c3aed',
      }}
    >
      TT-Platte
    </span>
  );
}

/** Liegt die Aussparung nach ihren durchbrochenen Bauteilen in einer TT-Platte? */
export function liegtInTtPlatte(
  bauteile: { bauart: string | null }[] | null | undefined,
): boolean {
  return (bauteile ?? []).some((b) => b.bauart === 'tt_platte');
}

/**
 * Die Medien durch eine Aussparung.
 *
 * Gemeldet am 18.08.: „die dämmungen und die restkanalstücke aus der liste
 * herausnehmen". Sie stehen weiterhin drin — der Nachweis darunter sagt als
 * Zahl mit Grund, was nicht als eigener Durchgang zählt. Gelöscht wird nichts.
 */
const MEDIENSPALTEN: Spalte<any>[] = [
  {
    schluessel: 'art', titel: 'Art', immerSichtbar: true, breite: '190px',
    wert: (m) => artname(m.art),
    inhalt: (m) => (
      <>
        {artname(m.art)}
        {m.istKlappe && (
          <> <StatusBadge zustand="info" text="Klappe" klein
                          titel="Brandschutzklappe — sie IST die Abschottung" /></>
        )}
        {m.vertrittRohr && (
          <span
            className="schwach"
            title={'Nur die Dämmung wurde gefunden, kein Rohr. Sie ist dicker als '
              + 'ihr Rohr — streift das Rohr die Aussparung knapp, schneidet nur '
              + 'sie. Diese Zeile vertritt das Rohr.'}
          >
            {' '}(vertritt das Rohr)
          </span>
        )}
      </>
    ),
  },
  {
    schluessel: 'gewerk', titel: 'Gewerk', breite: '110px',
    wert: (m) => m.gewerk ?? '',
    inhalt: (m) => m.gewerk ?? <em className="schwach">offen</em>,
  },
  {
    schluessel: 'system', titel: 'System',
    wert: (m) => m.systemtyp ?? m.typ ?? '',
    inhalt: (m) => <span className="schwach">{m.systemtyp ?? m.typ ?? '—'}</span>,
  },
  {
    schluessel: 'mass', titel: 'DN / B×H', ausrichtung: 'rechts', breite: '120px',
    wert: (m) => m.nennweite ?? Math.max(m.breiteMm ?? 0, m.hoeheMm ?? 0),
    inhalt: (m) => (m.nennweite ? `DN ${m.nennweite}`
      : m.breiteMm ? `${z(m.breiteMm, 0)}×${z(m.hoeheMm, 0)}`
        : '—'),
  },
  {
    schluessel: 'werkstoff', titel: 'Werkstoff', breite: '170px',
    wert: (m) => m.werkstoff ?? '',
    inhalt: (m) => (
      <span className="schwach">
        {m.werkstoff ?? '—'}
        {m.istBrennbar === true && (
          <> <StatusBadge zustand="warnung" text="brennbar" klein
                          titel="braucht eine Manschette" /></>
        )}
      </span>
    ),
  },
  {
    schluessel: 'daemmung', titel: 'Dämmung',
    wert: (m) => m.daemmungTyp ?? '',
    inhalt: (m) => (
      <span className="schwach">
        {m.daemmungTyp ?? '—'}
        {m.daemmstaerkeMm ? ` ${z(m.daemmstaerkeMm, 0)} mm` : ''}
      </span>
    ),
  },
];

/** Das durchbrochene Bauteil — Bauart und Dicke bestimmen das Schott. */
const BAUTEILSPALTEN: Spalte<any>[] = [
  {
    schluessel: 'typ', titel: 'Typ', immerSichtbar: true,
    wert: (h) => h.typ ?? h.kategorie ?? '',
    inhalt: (h) => h.typ ?? h.kategorie ?? '—',
  },
  {
    schluessel: 'bauart', titel: 'Bauart', breite: '150px',
    wert: (h) => bauartname(h.bauart),
    inhalt: (h) => <strong>{bauartname(h.bauart)}</strong>,
  },
  {
    schluessel: 'dicke', titel: 'Dicke', art: 'zahl', einheit: 'mm', nachkomma: 0,
    breite: '110px',
    wert: (h) => h.dickeMm,
  },
  {
    schluessel: 'feuer', titel: 'Feuerwiderstand', breite: '160px',
    wert: (h) => h.feuerwiderstand ?? '',
    inhalt: (h) => h.feuerwiderstand ?? '—',
  },
  {
    schluessel: 'tragend', titel: 'tragend', breite: '100px',
    wert: (h) => (h.istTragend === true ? 'ja' : h.istTragend === false ? 'nein' : ''),
    inhalt: (h) => (h.istTragend === true ? 'ja'
      : h.istTragend === false ? 'nein' : '—'),
  },
  {
    schluessel: 'herkunft', titel: 'Herkunft', breite: '130px',
    wert: (h) => herkunftname(h.bauartHerkunft),
    inhalt: (h) => (
      <span className="schwach" style={{ fontSize: 11 }}
            title={herkunftname(h.bauartHerkunft)}>
        {herkunftname(h.bauartHerkunft)}
      </span>
    ),
  },
];

export function AussparungArbeit({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [ansicht, setAnsicht] = useState<'baum' | 'anforderungen'>('baum');

  return (
    <>
      <div className="reiter" role="tablist" style={{ marginBottom: 14 }}>
        <button
          type="button" role="tab"
          aria-selected={ansicht === 'baum'}
          className={`reiter-knopf${ansicht === 'baum' ? ' aktiv' : ''}`}
          onClick={() => setAnsicht('baum')}
        >
          Nach Planungsbereich
        </button>
        <button
          type="button" role="tab"
          aria-selected={ansicht === 'anforderungen'}
          className={`reiter-knopf${
            ansicht === 'anforderungen' ? ' aktiv' : ''}`}
          onClick={() => setAnsicht('anforderungen')}
        >
          Offene Anforderungen
        </button>
      </div>

      {ansicht === 'baum'
        ? <Bereichsbaum api={api} projektId={projektId} />
        : <Anforderungen api={api} projektId={projektId} />}
    </>
  );
}

/**
 * Die vier Arten einer Verlaufszeile — und was sie wert sind.
 *
 * `zustand` und `abgleich` kommen aus der PLATTFORM: die Stilllegung einer
 * entfernten Aussparung (F87) und die Entscheidung des Fachplaners im
 * Abgleich (AS8). Sie sind der Nachweis, wer wann was festgelegt hat, und
 * standen bis AS8 hinter demselben Haken wie die elftausend
 * Statusmeldungen aus dem conVoid-Log.
 */
const VERLAUFSART: Record<string, { name: string; farbe: string }> = {
  zustand: { name: 'Zustand (Plattform)', farbe: 'var(--warnung)' },
  abgleich: { name: 'Abgleich (Entscheidung)', farbe: 'var(--akzent)' },
  anmerkung: { name: 'Anmerkung', farbe: 'var(--gut)' },
  system: { name: 'System', farbe: 'var(--linie)' },
};

/** Der Baum: Geschoss → Planungsbereich → Aussparung. */
function Bereichsbaum({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [baum, setBaum] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  // „Seit der letzten Aufnahme geändert" — der Filter, mit dem man nach
  // einem Modelllauf anfängt: Was ist neu, was wurde stillgelegt oder
  // reaktiviert, wo ist im Abgleich entschieden worden. Der Stichtag kommt
  // mit der Antwort; ohne ihn wäre die Zahl nicht nachrechenbar.
  const [nurGeaendert, setNurGeaendert] = useState(false);

  const laden = useCallback(() => {
    setFehler(null);
    api.aussparungBaumBereich(projektId, nurGeaendert)
      .then(setBaum).catch(setFehler);
  }, [api, projektId, nurGeaendert]);

  useEffect(() => { laden(); }, [laden]);

  const umschalten = (k: string) =>
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(k)) neu.delete(k); else neu.add(k);
      return neu;
    });

  if (fehler && !baum) {
    return (
      <ErrorState
        meldung="Der Baum lässt sich nicht laden."
        technisch={fehler}
        beiErneutVersuchen={laden}
      />
    );
  }

  if (!baum) {
    return <LoadingState text="Planungsbereiche werden geladen …" zeilen={8} />;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr',
                  gap: 16, alignItems: 'start' }}>
      <div className="karte" style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>
          Geschoss und Planungsbereich
          <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
            {' '}({baum.gesamt?.toLocaleString('de-DE')})
          </span>
        </h3>

        {/* --- Was hat sich seit der letzten Aufnahme getan? --- */}
        <label style={{ fontSize: 11, display: 'block', marginBottom: 8 }}
               className="schwach">
          <input
            type="checkbox"
            checked={nurGeaendert}
            onChange={(e) => { setNurGeaendert(e.target.checked);
                               setOffen(new Set()); }}
          />
          {' '}nur seit der letzten Aufnahme geändert
          {baum.letzteAufnahme && (
            <span> ({String(baum.letzteAufnahme).slice(0, 16)
                      .replace('T', ' ')} UTC)</span>
          )}
        </label>

        {nurGeaendert && (
          <p className="schwach" style={{ fontSize: 11, margin: '0 0 8px' }}>
            Neu aufgenommene Aussparungen und solche mit einer Verlaufszeile
            der Plattform ab diesem Stand — Stilllegung, Reaktivierung (F87)
            oder eine Entscheidung im Abgleich (AS8). Die Kommentare aus dem
            conVoid-Log zählen NICHT mit: Ihr Zeitpunkt ist der des
            Kommentars im Modell.
          </p>
        )}

        {(baum.geschosse ?? []).map((g: any) => {
          const gOffen = offen.has(g.geschoss);

          return (
            <div key={g.geschoss} style={{ marginBottom: 4 }}>
              <button
                type="button"
                onClick={() => umschalten(g.geschoss)}
                style={{
                  width: '100%', textAlign: 'left', padding: '5px 8px',
                  background: 'var(--flaeche)', border: '1px solid var(--linie)',
                  borderRadius: 4, cursor: 'pointer', fontSize: 13.5,
                }}
              >
                <span style={{ display: 'inline-block', width: 14 }}>
                  {gOffen ? '▾' : '▸'}
                </span>
                <strong>{g.geschoss}</strong>
                <span className="schwach" style={{ fontSize: 12 }}>
                  {' '}— {g.anzahl?.toLocaleString('de-DE')}
                </span>
              </button>

              {gOffen && (
                <div style={{ marginLeft: 16, marginTop: 3 }}>
                  {(g.bereiche ?? []).map((b: any) => {
                    const bk = `${g.geschoss}|${b.bereich}`;
                    const bOffen = offen.has(bk);

                    return (
                      <div key={b.bereich} style={{ marginBottom: 3 }}>
                        <button
                          type="button"
                          onClick={() => umschalten(bk)}
                          style={{
                            width: '100%', textAlign: 'left',
                            padding: '3px 8px', background: 'transparent',
                            border: '1px solid var(--linie)',
                            borderLeft: '3px solid var(--akzent)',
                            borderRadius: 3, cursor: 'pointer', fontSize: 12.5,
                          }}
                        >
                          <span style={{ display: 'inline-block', width: 14 }}>
                            {bOffen ? '▾' : '▸'}
                          </span>
                          <strong>{b.bereich}</strong>
                          <span className="schwach" style={{ fontSize: 11 }}>
                            {' '}— {b.anzahl}
                          </span>
                          {b.ohneAnforderung > 0 && (
                            <span style={{ color: 'var(--warnung)', fontSize: 11 }}>
                              {' '}· {b.ohneAnforderung} offen
                            </span>
                          )}
                        </button>

                        {bOffen && (
                          <div style={{ marginLeft: 16, marginTop: 2 }}>
                            {(b.aussparungen ?? []).slice(0, 300)
                              .map((a: any) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => setGewaehlt(a.id)}
                                style={{
                                  display: 'block', width: '100%',
                                  textAlign: 'left', padding: '2px 6px',
                                  background: gewaehlt === a.id
                                    ? 'var(--flaeche)' : 'transparent',
                                  border: 'none', cursor: 'pointer',
                                  fontSize: 12,
                                  borderBottom: '1px solid var(--linie)',
                                }}
                              >
                                <strong>
                                  {a.kennzeichen ?? a.revitElementId}
                                </strong>
                                {a.ttPlatte && <TtPlatteMarke />}
                                {!a.anforderungGeprueft && (
                                  <span style={{ marginLeft: 5 }}>
                                    <IconWarnung size={11} />
                                  </span>
                                )}
                                {a.medien > 0 && (
                                  <span className="schwach"
                                        style={{ fontSize: 11 }}>
                                    {' '}· {a.medien} Medium/Medien
                                  </span>
                                )}
                              </button>
                            ))}

                            {(b.aussparungen ?? []).length > 300 && (
                              <p className="schwach" style={{ fontSize: 11 }}>
                                Die ersten 300 von {b.aussparungen.length}.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {gewaehlt
        ? <Detail api={api} id={gewaehlt} />
        : (
          <EmptyState
            titel="Keine Aussparung gewählt."
            erklaerung={'Eine Aussparung links auswählen — die Ansicht zeigt sie dann '
              + 'vollständig: Verortung, Medien, durchbrochenes Bauteil, Freigabestand '
              + 'mit den conVoid-Kommentaren, die Verknüpfung zum Architekturmodell und '
              + 'die Zulassungen der Durchführungen.'}
          />
        )}
    </div>
  );
}

/** Eine Aussparung, vollständig. */
function Detail({ api, id }: { api: ApiClient; id: string }) {
  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [alleKommentare, setAlleKommentare] = useState(false);
  const [zeigeAlleZeilen, setZeigeAlleZeilen] = useState(false);

  useEffect(() => {
    setD(null);
    setFehler(null);
    api.aussparungVollstaendig(id).then(setD).catch(setFehler);
  }, [api, id]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Aussparung konnte nicht geladen werden."
        technisch={fehler}
      />
    );
  }
  if (!d) return <LoadingState text="Aussparung wird geladen …" zeilen={6} spalten={3} />;

  const a = d.aussparung;
  const von = (d.verortung ?? []).find((r: any) => r.seite === 'a');
  const nach = (d.verortung ?? []).find((r: any) => r.seite === 'b');

  // Die Raumnummer EINMAL - dieselbe Funktion wie im PDF.
  const raum = (r: any) => raumangabe(r?.raumnummer, r?.raumname);

  // Das Maß steckt jetzt im Anzeigenamen aus der API — an EINER Stelle
  // gebaut, dieselbe, die das Revit-Addin benutzt. Der Rückfall bleibt für
  // den Fall, dass eine ältere API antwortet.
  const anzeigename = d.anzeigename
    ?? [a.kennzeichen ?? a.revitElementId,
        a.durchmesserMm
          ? `ASP ⌀ ${z(a.durchmesserMm, 0)} mm`
          : a.breiteMm && a.hoeheMm
            ? `ASP ${z(a.breiteMm, 0)} x ${z(a.hoeheMm, 0)} mm`
            : null]
       .filter(Boolean).join(' · ');

  // ── Der Verlauf ────────────────────────────────────────────────────────
  //
  // Vier Arten in einer Zeitleiste, und sie sind NICHT gleich viel wert:
  //
  //   zustand    aus der Übernahme — stillgelegt / reaktiviert (F87)
  //   abgleich   die Entscheidung des Fachplaners (AS8) — welcher Stand gilt
  //   anmerkung  ein Mensch hat etwas geschrieben
  //   system     eine Statusmeldung des Werkzeugs
  //
  // Auf zweihundert echte Hinweise kommen elftausend Statusmeldungen;
  // deshalb sind die Systemmeldungen zunächst eingeklappt. Die Zeilen der
  // PLATTFORM (zustand, abgleich) sind IMMER zu sehen: Bis AS8 lagen sie
  // hinter demselben Haken wie die Statusmeldungen — eine Entscheidung mit
  // Namen und Datum, versteckt hinter „auch die Systemmeldungen".
  const verlauf = (d.freigabestand ?? []);

  const plattform = verlauf.filter(
    (v: any) => v.art === 'zustand' || v.art === 'abgleich');

  const anmerkungen = verlauf.filter((v: any) => v.art === 'anmerkung');
  const system = verlauf.filter((v: any) => v.art === 'system');

  const sichtbar = alleKommentare
    ? verlauf
    : [...plattform, ...anmerkungen];

  return (
    <div style={{ maxHeight: '78vh', overflowY: 'auto' }}>
      {/* 1. Die Aussparung */}
      <div className="karte">
        {/* Der Anzeigename kommt aus der API und wird dort an EINER Stelle
            gebaut — dieselbe, die das Revit-Addin benutzt. Stünde er hier
            noch einmal, hieße dieselbe Aussparung nach der ersten Änderung
            an zwei Orten anders.

            Die Nummer allein genügt nicht: Wer im Modell sucht, hat sie auf
            dem Zettel, aber vor sich ein Loch in der Wand. */}
        <h3 style={{ marginTop: 0 }}>
          {anzeigename}
          {liegtInTtPlatte(d.bauteile) && <TtPlatteMarke />}
          <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
            {a.form && ` · ${a.form}`}
            {a.tiefeMm && ` · Tiefe ${z(a.tiefeMm, 0)} mm`}
          </span>
        </h3>

        {/* 2. Die Verortung */}
        <PropertyGrid
          spalten={2}
          eintraege={[
            {
              beschriftung: 'Geschoss',
              wert: (
                <>
                  {a.geschoss ?? '—'}
                  {a.ebene && a.ebene !== a.geschoss && (
                    <span className="schwach"> ({a.ebene})</span>
                  )}
                </>
              ),
            },
            {
              beschriftung: 'Planungsbereich',
              wert: (
                <>
                  <strong>{a.planungsbereich ?? '—'}</strong>
                  {a.quadrant && <span className="schwach"> · {a.quadrant}</span>}
                </>
              ),
            },
            {
              beschriftung: 'von Raum nach Raum',
              wert: (
                <>{raum(von)} <span className="schwach">→</span> {raum(nach)}</>
              ),
              breit: true,
            },
            { beschriftung: 'Gewerk', wert: a.gewerke ?? a.gewerkRoh },
            {
              beschriftung: 'Brandschutzanforderung',
              wert: a.anforderungGeprueft
                ? <StatusBadge zustand="gut" text={a.anforderungGeprueft} klein />
                : (
                  <StatusBadge
                    zustand="fehler" klein
                    text={`nicht geprüft — im Modell „${a.anforderungRoh ?? 'nichts'}"`}
                  />
                ),
              breit: true,
            },
          ]}
        />
      </div>

      {/* 3. Die Medien */}
      <div className="karte">
        {/* ══════════════════════════════════════════════════════════════
            NUR DIE ZÄHLENDEN IN DER LISTE
            ══════════════════════════════════════════════════════════════

            Eine Dämmung neben ihrem Rohr ist kein zweites Medium, und ein
            Anschlusskanal neben einer Brandschutzklappe ist derselbe Kanal.

            Ausgezählt am 2026-08-17: 1.016 von 3.764 Durchgängen waren
            Dämmungen — jeder vierte, und jeder erzeugte eine zweite
            Schottzeile und eine zweite Katasternummer für EIN Rohr.

            Bis zum 18.08. standen sie durchgestrichen in DERSELBEN Liste. Das
            war als Nachweis gedacht und wirkte als Doppelung: ein Rohr mit
            Dämmung ergab zwei Zeilen, ein Kanal an einer Klappe bis zu drei.

            Gemeldet am 18.08.: „die dämmungen und die restkanalstücke aus der
            liste herausnehmen". Jetzt steht der Nachweis darunter — als Zahl
            mit Grund. Sie beantwortet „warum ein Schott und nicht zwei"
            genauso gut, ohne die Liste zu verdoppeln. Gelöscht wird nichts. */}
        <h4 style={{ marginTop: 0 }}>
          Medien durch die Aussparung
          <span className="schwach" style={{ fontWeight: 400, fontSize: 12 }}>
            {' '}({(d.medien ?? []).length})
          </span>
        </h4>

        {(d.medien ?? []).length === 0 ? (
          <EmptyState
            klein
            titel="Kein Medium."
            erklaerung={'Entweder ist die Aussparung Reserve — dann gehört sie '
              + 'verschlossen — oder das Fachmodell fehlt.'}
          />
        ) : (
          <DataTable
            spalten={MEDIENSPALTEN}
            zeilen={d.medien as any[]}
            schluessel={(m: any) => `${m.art}|${m.gewerk}|${m.nennweite}|${m.typ}|${m.systemtyp}`}
            suche={(d.medien as any[]).length > 8}
            spaltenwahl={false}
            dicht
          />
        )}

        {/* Der Nachweis: zusammengefasst, aufklappbar, standardmäßig zu.
            Wer die Zahl 'ein Schott' anzweifelt, findet hier, was
            weggefallen ist und warum. */}
        {(d.ausgefiltert?.anzahl ?? 0) > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary className="schwach"
                     style={{ fontSize: 11.5, cursor: 'pointer' }}>
              {d.ausgefiltert.anzahl} weitere Element(e) zählen nicht als
              eigener Durchgang und bekommen kein Schott
            </summary>

            <ul className="schwach" style={{ fontSize: 11.5, margin: '6px 0 0' }}>
              {d.ausgefiltert.gruende.map((g: any, i: number) => (
                <li key={i}>
                  <strong>{g.anzahl}×</strong>
                  {g.arten ? ` (${g.arten})` : ''} — {g.grund}
                </li>
              ))}
            </ul>

            <p className="sehr-schwach"
               style={{ fontSize: 11, margin: '6px 0 0' }}>
              Gelöscht wird nichts — die Zeilen stehen in der Datenbank und
              erscheinen wieder, sobald der Durchgangsfilter anders
              entscheidet.
            </p>
          </details>
        )}
      </div>

      {/* 4. Das durchbrochene Bauteil */}
      <div className="karte">
        <h4 style={{ marginTop: 0 }}>
          Durchbrochenes Bauteil
          <span className="schwach" style={{ fontWeight: 400, fontSize: 12 }}>
            {' '}({(d.bauteile ?? []).length})
          </span>
        </h4>

        {(d.bauteile ?? []).length === 0 ? (
          <EmptyState
            klein
            titel="Kein durchbrochenes Bauteil gefunden."
            erklaerung="Ohne Bauart lässt sich kein Schott wählen."
          />
        ) : (
          <DataTable
            spalten={BAUTEILSPALTEN}
            zeilen={d.bauteile as any[]}
            schluessel={(h: any) => `${h.typ ?? h.kategorie}|${h.dickeMm}|${h.bauart}`}
            suche={false}
            spaltenwahl={false}
            dicht
          />
        )}
      </div>

      {/* 5. Der Freigabestand */}
      <div className="karte">
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'baseline' }}>
          <h4 style={{ marginTop: 0 }}>
            Freigabestand
            <span className="schwach" style={{ fontWeight: 400,
                                               fontSize: 12 }}>
              {' '}· {a.zustand ?? a.zustandRoh ?? 'unbekannt'}
            </span>
          </h4>

          {system.length > 0 && (
            <label style={{ fontSize: 11 }} className="schwach">
              <input
                type="checkbox"
                checked={alleKommentare}
                onChange={(e) => setAlleKommentare(e.target.checked)}
              />
              {' '}auch die {system.length}
              {' '}Systemmeldungen
            </label>
          )}
        </div>

        {a.freigegebenVon && (
          <p style={{ fontSize: 12, margin: '0 0 8px' }}>
            freigegeben von <strong>{a.freigegebenVon}</strong>
          </p>
        )}
        {a.abgelehntVon && (
          <p style={{ fontSize: 12, margin: '0 0 8px', color: 'var(--schlecht)' }}>
            abgelehnt von <strong>{a.abgelehntVon}</strong>
          </p>
        )}
        {a.offenBei && (
          <p style={{ fontSize: 12, margin: '0 0 8px', color: 'var(--warnung)' }}>
            offen bei <strong>{a.offenBei}</strong>
          </p>
        )}

        {sichtbar.length === 0 ? (
          <p className="schwach" style={{ fontSize: 12, margin: 0 }}>
            Kein Eintrag im conVoid-Protokoll.
          </p>
        ) : (
          <div style={{ fontSize: 12 }} data-pruefung="verlauf">
            {(zeigeAlleZeilen ? sichtbar : sichtbar.slice(0, 40))
              .map((v: any, i: number) => (
              <div key={i} style={{
                padding: '5px 0',
                borderBottom: '1px solid var(--linie)',
                borderLeft: VERLAUFSART[v.art]
                  ? `3px solid ${VERLAUFSART[v.art].farbe}` : undefined,
                paddingLeft: VERLAUFSART[v.art] ? 8 : undefined,
                opacity: v.art === 'system' ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', gap: 8,
                              alignItems: 'baseline' }}>
                  <strong>{v.profil ?? '—'}</strong>
                  <span className="schwach" style={{ fontSize: 11 }}>
                    {v.zeitpunkt?.slice(0, 10)}
                  </span>
                  {v.urheber && (
                    <span className="schwach" style={{ fontSize: 11 }}>
                      · {v.urheber}
                    </span>
                  )}
                  <span className="schwach" style={{ fontSize: 10 }}>
                    {VERLAUFSART[v.art]?.name ?? v.art}
                  </span>
                </div>
                <div style={{ marginTop: 2 }}>{v.text}</div>
              </div>
            ))}

            {/* Kein stiller Schnitt: Die Zahl steht da, und der Rest ist
                einen Klick entfernt. „Die neuesten 40 von 412" ohne Weg zu
                den übrigen 372 ist dieselbe Falle wie ein Take(500). */}
            {sichtbar.length > 40 && !zeigeAlleZeilen && (
              <p style={{ fontSize: 11, marginTop: 6 }}>
                <span className="schwach">
                  Die neuesten 40 von {sichtbar.length}.
                </span>
                {' '}
                <button className="m-knopf" style={{ fontSize: 11 }}
                        onClick={() => setZeigeAlleZeilen(true)}>
                  alle {sichtbar.length} zeigen
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      {/* 6. Die Verknüpfung zum Architekturmodell */}
      <div className="karte">
        <h4 style={{ marginTop: 0 }}>Architekturmodell (ARDU)</h4>

        {(d.architektur ?? []).length === 0 ? (
          <p className="schwach" style={{ fontSize: 12, margin: 0 }}>
            Keine Entsprechung im Architekturmodell gefunden. Entweder ist
            die Aussparung dort noch nicht übernommen, oder die Paarung
            konnte sie nicht zuordnen.
          </p>
        ) : (
          (d.architektur ?? []).map((x: any, i: number) => {
            const paar = (d.paare ?? []).find(
              (p: any) => p.architekturId === x.id);

            return (
              <PropertyGrid
                key={i}
                spalten={2}
                eintraege={[
                  {
                    beschriftung: 'Element',
                    wert: (
                      <>
                        {x.kennzeichen ?? x.revitElementId}
                        <span className="schwach"> · {x.verknuepfung}</span>
                      </>
                    ),
                    breit: true,
                  },
                  {
                    beschriftung: 'Maße dort',
                    wert: `${x.durchmesserMm
                      ? `⌀ ${z(x.durchmesserMm, 0)}`
                      : `${z(x.breiteMm, 0)} × ${z(x.hoeheMm, 0)}`} mm`,
                  },
                  {
                    beschriftung: 'Abstand',
                    wert: paar ? (
                      <>
                        {z(paar.abstandMm, 0)} mm
                        {paar.befund && (
                          <> <StatusBadge zustand="warnung"
                                          text={paarungname(paar.befund)} klein /></>
                        )}
                      </>
                    ) : null,
                  },
                  { beschriftung: 'Zustand dort', wert: x.zustandRoh },
                  ...(x.anmerkung ? [{
                    beschriftung: 'Anmerkung',
                    wert: (
                      <>
                        {x.anmerkung}
                        <span className="schwach" style={{ fontSize: 11 }}>
                          {' '}({x.anmerkungFeld})
                        </span>
                      </>
                    ),
                    breit: true,
                  }] : []),
                ]}
              />
            );
          })
        )}
      </div>

      {/* 7. Die Zulassungen */}
      <ContentSection titel="Brandschott und Zulassung">
        {(d.medien ?? []).filter((m: any) => m.schott).length === 0 ? (
          <EmptyState
            klein
            titel="Noch keine Zuordnung."
            erklaerung={'Sie entsteht aus Medium, Bauart und Anforderung — fehlt eines '
              + 'davon, bleibt sie offen.'}
          />
        ) : (
          <DataTable
            spalten={ZULASSUNGSSPALTEN}
            zeilen={(d.medien as any[]).filter((m: any) => m.schott)}
            schluessel={(m: any) => `${m.art}|${m.nennweite}|${m.schott?.system}`}
            suche={false}
            spaltenwahl={false}
            dicht
          />
        )}
      </ContentSection>
    </div>
  );
}

/**
 * Brandschott und Zulassung je Medium.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DIE FEHLENDE BRANDSCHUTZKLAPPE STEHT ROT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Vorher bekam dieser Fall still ein Regelschott. Die Liste sah vollständig
 * aus, und niemand rüstete nach. Ein Schott, das eine fehlende Klappe
 * verdeckt, ist schlimmer als eine leere Zeile: Die leere sieht man.
 */
const ZULASSUNGSSPALTEN: Spalte<any>[] = [
  {
    schluessel: 'medium', titel: 'Medium', immerSichtbar: true, breite: '190px',
    wert: (m) => artname(m.art),
    inhalt: (m) => (
      <>
        {artname(m.art)}
        {m.nennweite && <span className="schwach"> DN {m.nennweite}</span>}
      </>
    ),
  },
  {
    schluessel: 'system', titel: 'System',
    wert: (m) => (m.schott?.istAnforderung === true
      ? (m.schott?.anforderungstext ?? 'Brandschutzklappe erforderlich!')
      : (m.schott?.system ?? '')),
    inhalt: (m) => {
      const s2 = m.schott;
      if (s2?.istAnforderung === true) {
        return (
          <StatusBadge
            zustand="fehler"
            text={s2.anforderungstext ?? 'Brandschutzklappe erforderlich!'}
          />
        );
      }
      return (
        <>
          {s2?.system ?? <em className="schwach">keines</em>}
          {s2?.hersteller && (
            <span className="schwach" style={{ fontSize: 11 }}>
              {' '}({s2.hersteller})
            </span>
          )}
        </>
      );
    },
  },
  {
    schluessel: 'zulassung', titel: 'Zulassung', breite: '190px',
    wert: (m) => m.schott?.zulassungNummer ?? '',
    inhalt: (m) => (
      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
        {m.schott?.zulassungNummer ?? '—'}
        {m.schott?.zulassungArt && (
          <span className="schwach"> {m.schott.zulassungArt}</span>
        )}
      </span>
    ),
  },
  {
    schluessel: 'nummer', titel: 'Nummer', breite: '190px',
    wert: (m) => m.nummer?.nummer ?? '',
    inhalt: (m) => (m.nummer?.nummer
      ? (
        <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>
          {m.nummer.nummer}
        </span>
      )
      : <em className="schwach">offen</em>),
  },
  {
    // Der Stand kommt aus dem Wörterbuch und nicht aus zwei Wörtern.
    // „Regelschott" stand bis AS1 an ALLEN drei übrigen Herkünften — auch
    // an `geprueft_ohne`, wo gar kein Schott einzubauen ist, und an
    // `anforderung`, wo eine Brandschutzklappe fehlt. Drei verschiedene
    // Aussagen unter einem Wort.
    schluessel: 'stand', titel: 'Stand', art: 'status', breite: '150px',
    wert: (m) => schottherkunftname(m.schott?.herkunft),
    inhalt: (m) => (
      <StatusBadge
        zustand={m.schott?.herkunft === 'gerechnet' ? 'gut' : 'warnung'}
        text={schottherkunftname(m.schott?.herkunft)} klein
        titel={m.schott?.befund ?? undefined}
      />
    ),
  },
  {
    // Der Prüfstand der Zulassungsdeckung (AS6). DREI Zustände, und der
    // dritte ist der ehrliche: „ungeprüft" heißt, dass die Angabe fehlt,
    // gegen die zu prüfen wäre — nicht, dass die Wahl falsch ist. Bis zum
    // 23.09.2026 stand an ALLEN 2.036 Katasterzeilen `ZulassungPasst = true`,
    // gesetzt als Literal bei der Nummernvergabe.
    //
    // Der Grund steht im Tooltip und nicht in der Spalte: Er ist ein ganzer
    // Satz („Bauteildicke: 75 mm unter der kleinsten vom Bescheid gedeckten
    // Dicke von 100 mm"), und eine abgeschnittene Begründung ist keine.
    schluessel: 'pruefstand', titel: 'Prüfstand', art: 'status',
    breite: '150px',
    wert: (m) => zulassungsstand(m.schott?.zulassungPasst, m.schott?.bauart),
    inhalt: (m) => (m.schott
      ? (
        <StatusBadge
          zustand={zulassungsstandZustand(
            m.schott.zulassungPasst, m.schott.bauart)}
          text={zulassungsstand(m.schott.zulassungPasst, m.schott.bauart)}
          klein
          titel={m.schott.zulassungBefund ?? undefined}
        />
      )
      : <em className="schwach">—</em>),
  },
];

/**
 * Die offenen Brandschutzanforderungen — die Liste für die Architektur.
 *
 * Je Planungsbereich als PDF, weil die Rückmeldung abschnittsweise läuft:
 * Eine Datei mit 5.389 Zeilen käme nie zurück.
 */
function Anforderungen({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [bereiche, setBereiche] = useState<Bereich[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string>('');
  const [liste, setListe] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    api.aussparungBereiche(projektId).then(setBereiche).catch(setFehler);
  }, [api, projektId]);

  useEffect(() => {
    setListe(null);
    setFehler(null);
    api.aussparungAnforderungen(projektId, gewaehlt || undefined)
      .then(setListe)
      .catch(setFehler);
  }, [api, projektId, gewaehlt]);

  const offenGesamt = (bereiche ?? [])
    .reduce((s2, b) => s2 + b.ohneAnforderung, 0);

  const spalten: Spalte<any>[] = [
    {
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, breite: '170px',
      wert: (r) => (r.bauteil?.bauart === 'tt_platte'
        ? `${r.kennzeichen ?? r.revitElementId} TT-Platte`
        : r.kennzeichen ?? r.revitElementId),
      inhalt: (r) => (
        <>
          <strong>{r.kennzeichen ?? r.revitElementId}</strong>
          {r.bauteil?.bauart === 'tt_platte' && <TtPlatteMarke />}
        </>
      ),
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss', breite: '110px',
      wert: (r) => r.geschoss ?? '',
      inhalt: (r) => r.geschoss ?? '—',
    },
    {
      schluessel: 'bereich', titel: 'Bereich', breite: '130px',
      wert: (r) => r.planungsbereich ?? '',
      inhalt: (r) => r.planungsbereich ?? '—',
    },
    {
      schluessel: 'masse', titel: 'Maße', einheit: 'mm', ausrichtung: 'rechts',
      breite: '130px',
      wert: (r) => r.durchmesserMm ?? Math.max(r.breiteMm ?? 0, r.hoeheMm ?? 0),
      inhalt: (r) => (r.durchmesserMm
        ? `⌀ ${z(r.durchmesserMm, 0)}`
        : `${z(r.breiteMm, 0)}×${z(r.hoeheMm, 0)}`),
    },
    {
      schluessel: 'vonRaum', titel: 'von Raum',
      wert: (r) => r.vonRaum?.raumnummer ?? '',
      inhalt: (r) => (
        <span className="schwach">
          {raumangabe(r.vonRaum?.raumnummer, r.vonRaum?.raumname)}
        </span>
      ),
    },
    {
      schluessel: 'nachRaum', titel: 'nach Raum',
      wert: (r) => r.nachRaum?.raumnummer ?? '',
      inhalt: (r) => (
        <span className="schwach">
          {raumangabe(r.nachRaum?.raumnummer, r.nachRaum?.raumname)}
        </span>
      ),
    },
    {
      schluessel: 'bauteil', titel: 'durchbrochenes Bauteil',
      wert: (r) => r.bauteil?.typ ?? '',
      inhalt: (r) => (
        <span className="schwach">
          {r.bauteil?.typ ?? '—'}
          {r.bauteil?.bauart && (
            <span> ({bauartname(r.bauteil.bauart)})</span>
          )}
        </span>
      ),
    },
    {
      schluessel: 'roh', titel: 'im Modell', art: 'status', breite: '140px',
      wert: (r) => r.anforderungRoh ?? '',
      inhalt: (r) => (
        <StatusBadge
          zustand="fehler" klein
          text={r.anforderungRoh ?? 'leer'}
          titel="Ohne Feuerwiderstandsklasse lässt sich kein Schott bemessen"
        />
      ),
    },
  ];

  return (
    <ContentSection
      titel="Offene Brandschutzanforderungen"
      beschreibung={'Bei diesen Aussparungen steht im Modell „TBC?" — to be confirmed — '
        + 'oder das Feld ist leer. Ohne die Feuerwiderstandsklasse lässt sich kein '
        + 'Schott bemessen: Conlit und die Hilti-Systeme haben je Klasse verschiedene '
        + 'Prüfzeugnisse. Die Liste geht an die Architektur; sie enthält deshalb die '
        + 'Verortung, den Raum auf beiden Seiten und die durchbrochene Wand — eine '
        + 'Liste mit bloßen Kennungen käme zurück, wie sie ging.'}
      nackt
    >
      {fehler !== null && (
        <ErrorState
          meldung="Die offenen Anforderungen konnten nicht geladen werden."
          technisch={fehler}
        />
      )}

      {!bereiche?.some((b) => b.planungsbereich) && (
        <div className="hinweis" style={{ fontSize: 12 }}>
          Noch kein Planungsbereich zugeordnet. Er entsteht aus den
          Flächenplänen im Aussparungsmodell (<code>TuV_Planungsbereich</code>)
          und braucht einen neuen Modelllauf.
        </div>
      )}

      <DataTable
        name="Offene Brandschutzanforderungen"
        spalten={spalten}
        zeilen={(liste?.zeilen ?? []) as any[]}
        schluessel={(r: any) => r.id}
        laedt={liste === null && fehler === null}
        ausfuhr
        ausfuhrDateiname="Offene-Brandschutzanforderungen"
        // Die Trefferzahl kommt vom Server; die Tabelle zeigt, was geliefert
        // wurde, und das PDF enthält alle.
        trefferText={liste
          ? `${(liste.gesamt ?? 0).toLocaleString('de-DE')} offen`
          : undefined}
        werkzeuge={(
          <>
            <select value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)}
                    aria-label="Planungsbereich">
              <option value="">alle Planungsbereiche ({offenGesamt})</option>
              {(bereiche ?? [])
                .filter((b) => b.planungsbereich)
                .map((b) => (
                  <option key={b.planungsbereich!} value={b.planungsbereich!}>
                    {b.planungsbereich} — {b.ohneAnforderung} von {b.stueck} offen
                  </option>
                ))}
            </select>

            <AnforderungslisteKnopf
              api={api}
              projektId={projektId}
              bereich={gewaehlt || undefined}
              gesamtOffen={liste?.gesamt}
            />

            {/* AS7: Der Plan neben der Liste. Die Liste sagt WELCHE, der
                Plan sagt WO - und die Architektur sucht im Grundriss. */}
            <AussparungsplanKnopf
              api={api}
              projektId={projektId}
              bereich={gewaehlt || undefined}
            />
          </>
        )}
        leer={(
          <EmptyState
            titel="Keine offene Anforderung."
            erklaerung={gewaehlt
              ? 'In diesem Planungsbereich ist jede Anforderung geprüft.'
              : 'Jede Aussparung trägt eine geprüfte Feuerwiderstandsklasse.'}
            aktion={gewaehlt ? (
              <button className="m-knopf" onClick={() => setGewaehlt('')}>
                Alle Planungsbereiche zeigen
              </button>
            ) : undefined}
          />
        )}
      />
    </ContentSection>
  );
}

function z(x: number | null | undefined, n = 1): string {
  return x == null ? '—' : x.toLocaleString('de-DE', {
    minimumFractionDigits: n, maximumFractionDigits: n });
}
